import { useSeo } from '../lib/seo';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Sparkles, Clock, Calendar, Car, Compass, Coffee } from 'lucide-react';
import api from '../lib/api';
import NewsletterSignup from '../components/NewsletterSignup'; // eslint-disable-line no-unused-vars

function PlanEmailForm({ result }) {
  const [email, setEmail] = React.useState('');
  const [state, setState] = React.useState('idle'); // idle|sending|sent|error
  const [err, setErr] = React.useState('');
  async function send(e) {
    e.preventDefault();
    if (!email) return;
    setState('sending'); setErr('');
    try {
      await api.post('/plan/email', { email, title: result.title, stops: result.stops });
      api.post('/newsletter', { email, source: 'plan-my-day' }).catch(() => {});
      setState('sent');
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Could not send — please try again.');
      setState('error');
    }
  }
  if (state === 'sent') {
    return <div className="text-forest font-semibold" data-testid="plan-email-sent">Sent! Check your inbox — your plan is on its way.</div>;
  }
  return (
    <form onSubmit={send} className="flex flex-col sm:flex-row gap-3 max-w-xl">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
        data-testid="plan-email-input" className="flex-1 bg-white border border-charcoal/15 rounded-full px-5 py-3 text-sm" />
      <button type="submit" disabled={state === 'sending'} data-testid="plan-email-send"
        className="bg-heritage text-base px-6 py-3 rounded-full font-semibold hover:bg-heritage/90 disabled:opacity-60">
        {state === 'sending' ? 'Sending…' : 'Email me this plan'}
      </button>
      {state === 'error' && <div className="text-heritage text-sm self-center" data-testid="plan-email-error">{err}</div>}
    </form>
  );
}
import { AccessibilityDisclaimer } from '../components/Accessibility';

const MOODS = [
  { value: 'family', label: 'Family fun', icon: '🎈' },
  { value: 'outdoors', label: 'Big outdoors', icon: '🌲' },
  { value: 'heritage', label: 'Castles & kings', icon: '🏰' },
  // 'foodie' removed — the existing attraction tagging doesn't surface enough
  // venues for foodie days; was returning blank itineraries. Bring back once
  // food_drink venues are tagged broadly enough to fill a 3+ stop plan.
  { value: 'rainy-day', label: 'Rainy day', icon: '🌧️' },
  { value: 'romantic', label: 'Romantic', icon: '💕' },
];

const BUDGETS = [
  { value: 'free', label: 'Free' },
  { value: 'any', label: 'Any budget' },
];

const DURATIONS = [
  { value: 180, label: '~3 hours' },
  { value: 300, label: 'Half-day (~5h)' },
  { value: 480, label: 'Full day (~8h)' },
  { value: 600, label: 'Long day (~10h)' },
];

// Stop-time math: durations only, no absolute clocks. The first stop is
// "From the start" then each subsequent stop adds drive + dwell.
function elapsedAtArrival(stops, i) {
  let mins = 0;
  for (let j = 0; j < i; j++) {
    mins += (stops[j].dwell_minutes || 0) + (stops[j + 1]?.drive_minutes_from_prev || 0);
  }
  return mins;
}

function formatDuration(mins) {
  if (mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export default function PlanMyDay() {
  useSeo(
    'Plan My Day — Leicestershire Day Out Builder | Go Leicestershire',
    'Tell us who you are and what you love — we build you a ready-made Leicestershire day out with venues, timings and travel.'
  );
  const [mood, setMood] = useState('family');
  const [postcode, setPostcode] = useState('');
  const [budget, setBudget] = useState('any');
  const [duration, setDuration] = useState(480);
  // Access need toggle — when true, restricts the candidate pool to venues
  // with `accessibility.step_free_entrance = true` and passes the flag to the
  // backend planner so it can also weight nearby accessible parking.
  const [stepFree, setStepFree] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function generate() {
    setLoading(true);
    try {
      const r = await api.post('/plan-my-day', {
        mood, postcode, budget, duration_minutes: duration,
        step_free: stepFree || undefined,
      });
      setResult(r.data);
    } finally { setLoading(false); }
  }

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16">
      <div className="label text-heritage mb-3 inline-flex items-center gap-2"><Sparkles size={14}/> Plan My Day</div>
      <h1 className="font-display text-5xl lg:text-7xl leading-[0.95] mb-12">Tell us a mood. <em className="not-italic text-heritage">We&apos;ll do the rest.</em></h1>

      <div className="bg-white border border-charcoal/10 rounded-[2rem] p-8 lg:p-12 shadow-soft">
        <div className="label text-charcoal/60 mb-4">1. Pick a mood</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10">
          {MOODS.map((m) => (
            <button key={m.value} onClick={() => setMood(m.value)} data-testid={`mood-${m.value}`}
              className={`p-5 rounded-3xl text-left transition-all ${mood === m.value ? 'bg-forest text-base scale-[1.02]' : 'bg-base hover:bg-stone border border-charcoal/10'}`}>
              <div className="text-3xl mb-2">{m.icon}</div>
              <div className="font-semibold">{m.label}</div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <div className="label text-charcoal/60 mb-3">2. Postcode (optional)</div>
            <input value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} placeholder="LE11 2PY"
              className="w-full bg-base border border-charcoal/15 rounded-full px-6 py-4 focus:outline-none focus:border-forest"
              data-testid="postcode-input"/>
          </div>
          <div>
            <div className="label text-charcoal/60 mb-3">3. Budget</div>
            <div className="flex gap-2">
              {BUDGETS.map((b) => (
                <button key={b.value} onClick={() => setBudget(b.value)} data-testid={`budget-${b.value}`}
                  className={`flex-1 px-5 py-4 rounded-full font-semibold transition-colors ${budget === b.value ? 'bg-charcoal text-base' : 'bg-base border border-charcoal/15 hover:border-charcoal'}`}>
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-10">
          <div className="label text-charcoal/60 mb-3">4. How long have you got?</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {DURATIONS.map((d) => (
              <button
                key={d.value}
                onClick={() => setDuration(d.value)}
                data-testid={`duration-${d.value}`}
                className={`px-4 py-3 rounded-full font-semibold text-sm transition-colors ${duration === d.value ? 'bg-charcoal text-base' : 'bg-base border border-charcoal/15 hover:border-charcoal'}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Step-free toggle suspended pending better data coverage.
        <div className="mb-6">
          <label className="flex items-start gap-3 p-4 bg-base border border-charcoal/15 rounded-2xl cursor-pointer hover:border-forest/60 transition-colors">
            <input
              type="checkbox"
              checked={stepFree}
              onChange={(e) => setStepFree(e.target.checked)}
              data-testid="plan-step-free-toggle"
              className="mt-1 w-4 h-4 accent-forest"
            />
            <div className="min-w-0">
              <div className="font-semibold text-sm">I need step-free access</div>
              <div className="text-xs text-muted mt-1">
                We&apos;ll only include venues where step-free entry has been confirmed.
                Always double-check anything critical with the venue before you travel — provision can change.
              </div>
            </div>
          </label>
        </div>
        */}

        <button onClick={generate} disabled={loading} data-testid="plan-submit"
          className="bg-heritage text-base px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-heritage-deep transition-all hover:scale-[1.03] disabled:opacity-60">
          {loading ? 'Curating your day...' : 'Plan my day'} <ArrowRight size={16}/>
        </button>
      </div>

      {result && (
        <div className="mt-16" data-testid="plan-result">
          {result.step_free && (
            <div className="mb-6">
              <AccessibilityDisclaimer variant="block" />
            </div>
          )}
          {result.stops?.length === 0 ? (
            <div className="bg-white border border-dashed border-charcoal/15 rounded-3xl p-8 md:p-12 text-center" data-testid="plan-empty">
              <Sparkles size={28} className="mx-auto text-muted/40 mb-4"/>
              <h2 className="font-display text-2xl lg:text-3xl mb-3">No stops matched.</h2>
              <p className="text-muted max-w-md mx-auto">{result.tip}</p>
              {budget === 'free' && (
                <button onClick={() => { setBudget('any'); }} data-testid="plan-switch-any" className="mt-6 bg-charcoal text-base px-6 py-3 rounded-full font-semibold">
                  Switch to any budget
                </button>
              )}
            </div>
          ) : (
            <article className="bg-white border border-charcoal/10 rounded-[2rem] overflow-hidden shadow-soft">
              {/* Magazine masthead */}
              <header className="bg-charcoal text-base p-8 md:p-12">
                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <span className="label text-mustard">{result.for_day === 'tomorrow' ? `Tomorrow (${result.weekday})` : `${result.weekday}'s plan`}</span>
                  {result.weather_label && (
                    <span className="text-sm bg-white/10 text-base px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                      <Compass size={12}/> {result.weather_label}
                    </span>
                  )}
                  <span className="text-sm bg-white/10 text-base px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                    <Car size={12}/> {result.total_drive_minutes} min driving · {result.total_miles} mi
                  </span>
                </div>
                <h2 className="font-display text-3xl lg:text-5xl leading-[1.05]">
                  {result.stops.length} stops, plotted to loop back where you started.
                </h2>
                {result.opening && (
                  <p className="text-lg md:text-xl leading-relaxed mt-6 text-base/90 max-w-3xl" data-testid="plan-opening">
                    {result.opening}
                  </p>
                )}
              </header>

              {/* Stops as a narrative list with route markers */}
              <ol className="relative">
                {result.stops.map((s, i) => (
                  <li key={i} className="grid grid-cols-[2.5rem,1fr] md:grid-cols-[4rem,1fr] gap-0 border-t border-charcoal/10 first:border-t-0" data-testid={`plan-stop-${i}`}>
                    {/* Route gutter */}
                    <div className="flex flex-col items-center pt-8 pb-4">
                      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-display text-xl font-bold ${s.kind === 'event' ? 'bg-mustard text-charcoal' : 'bg-heritage text-base'}`}>
                        {i + 1}
                      </div>
                      {i < result.stops.length - 1 && (
                        <div className="flex-1 w-0.5 bg-charcoal/15 mt-2" aria-hidden />
                      )}
                    </div>
                    <div className="py-8 pr-6 md:pr-12">
                      {i > 0 && (
                        <div className="label text-charcoal/40 mb-3 inline-flex items-center gap-1.5" data-testid={`plan-hop-${i}`}>
                          <Car size={11}/> {s.drive_minutes_from_prev} min · {s.miles_from_prev} mi from the last stop
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-3 flex-wrap text-xs">
                        <span className="bg-charcoal text-base px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 font-semibold" data-testid={`plan-stop-elapsed-${i}`}>
                          <Clock size={11}/>
                          {i === 0 ? 'From the off' : `After ${formatDuration(elapsedAtArrival(result.stops, i))}`}
                        </span>
                        {s.dwell_minutes ? (
                          <span className="bg-stone text-charcoal/70 px-2.5 py-1 rounded-full" data-testid={`plan-stop-dwell-${i}`}>
                            ≈ {s.dwell_minutes} min visit
                          </span>
                        ) : null}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-6 items-start">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            {s.kind === 'event' && (
                              <span className="bg-mustard/20 text-charcoal text-xs uppercase tracking-wider font-bold px-2 py-1 rounded-md inline-flex items-center gap-1.5">
                                <Calendar size={11}/> On this week
                              </span>
                            )}
                            {s.category === 'food_drink' || (s.category || '').toLowerCase().includes('food') ? (
                              <span className="bg-forest/15 text-forest text-xs uppercase tracking-wider font-bold px-2 py-1 rounded-md inline-flex items-center gap-1.5">
                                <Coffee size={11}/> Food &amp; drink
                              </span>
                            ) : null}
                          </div>
                          <h3 className="font-display text-2xl md:text-3xl leading-tight mb-2">{s.name}</h3>
                          {s.town && (
                            <div className="text-muted text-sm mb-3 inline-flex items-center gap-1.5"><MapPin size={12}/> {s.town}</div>
                          )}
                          <p className="text-charcoal/85 leading-relaxed">{s.blurb}</p>
                          {s.slug && s.kind !== 'event' && (
                            <Link to={`/explore/${s.slug}`} data-testid={`plan-stop-link-${i}`} className="mt-4 inline-flex items-center gap-2 font-semibold text-heritage hover:underline">
                              Full details <ArrowRight size={14}/>
                            </Link>
                          )}
                          {s.kind === 'event' && s.url && (
                            <a href={s.url} target="_blank" rel="noreferrer noopener" data-testid={`plan-stop-event-${i}`} className="mt-4 inline-flex items-center gap-2 font-semibold text-heritage hover:underline">
                              Book / info <ArrowRight size={14}/>
                            </a>
                          )}
                        </div>
                        {s.hero_image && (
                          <Link
                            to={s.kind === 'event' ? '#' : `/explore/${s.slug}`}
                            className="block md:w-56 lg:w-64 aspect-[4/3] rounded-2xl overflow-hidden group shrink-0"
                            onClick={(e) => { if (s.kind === 'event') e.preventDefault(); }}
                          >
                            <img src={s.hero_image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                          </Link>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>

              {/* Closing line */}
              <footer className="bg-stone p-8 md:p-12 border-t border-charcoal/10">
                {result.closing ? (
                  <p className="font-display text-xl md:text-2xl leading-relaxed text-charcoal" data-testid="plan-closing">
                    {result.closing}
                  </p>
                ) : (
                  <p className="text-muted italic">Tap any stop above to plan the route in detail.</p>
                )}
                <div className="mt-6 flex items-center gap-3 text-sm text-muted">
                  <Clock size={14}/> Plan generated for {result.for_day === 'tomorrow' ? `tomorrow (${result.weekday})` : result.weekday}. Re-run anytime — weather and openings change daily.
                </div>
                {result.disclaimer && (
                  <div
                    data-testid="plan-disclaimer"
                    className="mt-6 pt-6 border-t border-charcoal/10 text-xs text-charcoal/55 italic leading-relaxed max-w-3xl"
                  >
                    {result.disclaimer}
                  </div>
                )}
              </footer>
            </article>
          )}

          {/* Email-me-this-itinerary (sends the actual plan, then subscribes) */}
          {result && (result.stops || []).length > 0 && (
            <div className="mt-8 bg-stone/60 border border-charcoal/10 rounded-3xl p-7 md:p-10" data-testid="plan-email-signup">
              <div className="label text-heritage mb-2">Save this plan</div>
              <h3 className="font-display text-2xl md:text-3xl mb-3">Want this in your inbox?</h3>
              <p className="text-charcoal/80 mb-5 max-w-xl text-base md:text-lg">
                We&apos;ll email you this exact plan + add you to the Friday digest. Subscribers go into our quarterly StayPlayExplore prize draw. One click to unsubscribe.
              </p>
              <PlanEmailForm result={result} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
